import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosInstance } from 'axios';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { errorMessageAdapter } from '../../../adapters';
import { MainCustomButton, SecondaryCustomButton } from '../../../components/Buttons';
import { StepperItem } from '../../../components/Stepper';
import { DashboardContainer, PageHeadline } from '../../../components/atoms';
import { Checkbox } from '../../../components/atoms/Checkbox';
import { CompanyRegisteringService } from '../../../services/CompanyRegisteringService';
import { ISICCodeSection, SICCodeService } from '../../../services/SICCodesService';
import { useAuth } from '../../../store/auth';
import { errorToast } from '../../../utils/toasts';
import { useApi } from '../../../services/hooks/apiHook';
import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';
import { defaultColors } from '../../../styles/style-guide';
import { Steps, useRegisteringStore } from './store/registering.store';
import { ShareholdersRegisteringService } from '../../../services/ShareholdersService';

interface IFormData {
  slugs: string[];
  terms: boolean;
}

const DashboardRegisteringBusinessPage: React.FunctionComponent = () => {
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [sicCodeSections, setSicCodeSections] = useState<ISICCodeSection[]>([]);

  const navigate = useNavigate();
  const { token, company, user } = useAuth();
  const { t, i18n } = useTranslation();
  const { setStep } = useRegisteringStore();

  const api: AxiosInstance = useApi();

  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);

  const primaryColor = colors.primary || defaultColors.primary;
  const isDelaware = user?.jurisdiction?.description === 'Delaware';

  const lastSicCodesFetchKeyRef = useRef<string | null>(null);
  const initialLanguageSyncRef = useRef(true);

  // Memoize headers to prevent unnecessary re-renders
  const headers = useMemo(
    () => ({
      'Content-Language': legalPersonalitySlug || i18n.language.toLowerCase(),
      Authorization: `Bearer ${token}`
    }),
    [legalPersonalitySlug, i18n.language, token]
  );

  // Fetch SIC codes with debounce on language change
  const fetchSicCodeSections = useCallback(async () => {
    if (!token) {
      return;
    }

    const currentLanguage = legalPersonalitySlug || i18n.language.toLowerCase();
    const fetchKey = `${token}|${currentLanguage}`;
    if (lastSicCodesFetchKeyRef.current === fetchKey) {
      return;
    }

    lastSicCodesFetchKeyRef.current = fetchKey;

    try {
      console.log('📡 [Registering Business] Buscando SIC sections com idioma:', currentLanguage);
      const { data } = await SICCodeService.listSicCodeSections(api, {
        headers: {
          'Content-Language': currentLanguage,
          Authorization: `Bearer ${token}`
        }
      });
      console.log('✅ [Registering Business] SIC sections recebidas:', data);
      setSicCodeSections(data);
    } catch (error) {
      console.error('❌ [Registering Business] Erro ao buscar SIC code sections:', error);
      errorToast(t('messages.errorLoadingDocuments') || 'Error loading SIC codes');
    }
  }, [token, api, legalPersonalitySlug, i18n.language, t]);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (initialLanguageSyncRef.current) {
      initialLanguageSyncRef.current = false;
      void fetchSicCodeSections();
      return;
    }

    const timeout = setTimeout(() => {
      console.log('🔄 [Registering Business] Refazendo requisição de SIC sections devido à mudança de idioma:', legalPersonalitySlug || i18n.language);
      void fetchSicCodeSections();
    }, 700);

    return () => {
      clearTimeout(timeout);
    };
  }, [token, legalPersonalitySlug, i18n.language, fetchSicCodeSections]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        slugs: Yup.array()
          .of(Yup.string())
          .min(1)
          .required(t('Required') || ''),
        terms: Yup.boolean()
          .oneOf([true], t('Required') || '')
          .required(t('Required') || '')
      }),
    [i18n.language, t]
  );

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    getValues,
    trigger
  } = useForm<IFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: { slugs: [] },
    mode: 'onSubmit'
  });

  // Re-validate on language change only after errors already exist (never on mount)
  const prevLanguageRef = useRef(i18n.language);
  useEffect(() => {
    if (prevLanguageRef.current === i18n.language) return;
    prevLanguageRef.current = i18n.language;
    if (Object.keys(errors).length > 0) {
      void trigger();
    }
  }, [i18n.language, trigger, errors]);


  const onSubmit = async (data: IFormData) => {
    try {
      const payload = {
        company_slug: company!.slug,
        sic_codes: data.slugs.map((slug) => ({ sic_code_slug: slug }))
      };

      await CompanyRegisteringService.registerSicCompany(api, payload, { headers });

      localStorage.setItem('dashboard_show_finish_register_congrats', '1');
      navigate('/dashboard', { state: { fromRegistering: true } });
    } catch (e: any) {
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  const handleSaveBackStepMembers = async () => {
    const isDelaware = user?.jurisdiction?.description === 'Delaware';

    const response = await ShareholdersRegisteringService.returningToPreviousStep(api, null, { headers });
    if (response.success === false) {
      console.error('Failed to return to the previous step Directors', response.error);
    }

    if (isDelaware) {
      setStep(Steps.Members);
      return; // Interrompe a execução para não sobrescrever o step
    }

    setStep(Steps.Shareholders);
  };

  return (
    <div className="flex flex-col gap-6 pb-8">
      <PageHeadline title={t('others.othRegistering')} />

      <div className="lg:10/12 flex w-full flex-row flex-wrap items-center justify-between gap-8 lg:flex-row">
        <StepperItem text={t('others.othCompany')} step={1} active onClick={() => setStep(Steps.Company)} />
        {!isDelaware && (
          <>
            <StepperItem text={t('others.othDirectors')} step={2} active />
            <StepperItem text={t('others.othShareholders')} step={3} active />
          </>
        )}
        {isDelaware && <StepperItem text={t('descriptions.Members')} step={2} active />}
        <StepperItem text={t('others.othBusiness')} step={!isDelaware ? 4 : 3} active />
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
          <DashboardContainer title={t('others.othNatureOfBusiness') || ''}>
            <div className="flex w-full justify-between font-medium">
              <span className="w-full">{t('others.othCategory')}</span>
              <span className="w-full">{t('others.othDescription')}</span>
            </div>

            <div className="flex flex-col gap-4">
              {sicCodeSections.map((section) => {
                return section.sicCodes.map((sic) => (
                  <div key={sic.id} className="flex w-full items-center justify-between">
                    <Checkbox
                      containerClassName="flex-1"
                      label={section.description}
                      checked={selectedSlugs.includes(sic.slug)}
                      onChange={() => {
                        if (selectedSlugs.includes(sic.slug)) {
                          const updatedSlugs = selectedSlugs.filter((s) => s !== sic.slug);
                          setValue('slugs', updatedSlugs);
                          setSelectedSlugs(updatedSlugs);
                          return;
                        }
                        const updatedSlugs = [...selectedSlugs, sic.slug];
                        setValue('slugs', updatedSlugs);
                        setSelectedSlugs(updatedSlugs);
                      }}
                    />

                    <span className="flex-1">{sic.description}</span>
                  </div>
                ));
              })}
            </div>
          </DashboardContainer>
        </div>

        <DashboardContainer title={t('others.othDeclarationAndConfirmation') || ''}>
          <span className="text-sm text-[#1D1D1D]">{t('others.othDeclarationText')}</span>

          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              <Checkbox
                label={t('others.othIDeclareAndConfirmAsAbove') || ''}
                {...field}
                error={errors.terms?.message}
                value="true"
              />
            )}
          />
        </DashboardContainer>
        <div className="h- flex justify-between gap-4 border-t-4 bg-white p-4">
          <SecondaryCustomButton
            style={{ height: 46, fontSize: 16 }}
            type="button"
            onClick={handleSaveBackStepMembers}
            text={t('buttons.btnBack')}
          />
          <MainCustomButton text={t('buttons.btnNext')} type="submit" disabled={!getValues('slugs').length} />
        </div>
      </form>
    </div>
  );
};

export { DashboardRegisteringBusinessPage };
